import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { ArrowLeft, Calendar, Clock, Tag, Share2, Facebook, Twitter, Linkedin, AlertCircle, MessageSquare } from 'lucide-react';
import Button from '../components/ui/Button';
import { CREATE_MAILING_LIST_SUBSCRIPTION } from '../graphql/mutations/createMailingListSubscription';

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterError, setNewsletterError] = useState('');
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);

  const [createMailingListSubscription, { loading: isSubscribing }] = useMutation(CREATE_MAILING_LIST_SUBSCRIPTION);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewsletterError('');
    setNewsletterSuccess(false);

    if (!validateEmail(newsletterEmail)) {
      setNewsletterError('Indtast venligst en gyldig email-adresse.');
      return;
    }

    try {
      const { data } = await createMailingListSubscription({
        variables: {
          input: {
            email: newsletterEmail,
          },
        },
      });

      if (data?.createMailingListSubscription?.mailingListSubscription) {
        setNewsletterSuccess(true);
        setNewsletterEmail('');
      } else if (data?.createMailingListSubscription?.errors) {
        setNewsletterError(data.createMailingListSubscription.errors);
      } else {
        setNewsletterError('Kunne ikke tilmelde nyhedsbrev. Pr\u00f8v venligst igen.');
      }
    } catch (err) {
      setNewsletterError('Kunne ikke tilmelde nyhedsbrev. Pr\u00f8v venligst igen.');
    }
  };

  // Mock post data - in a real app, this would come from an API
  const mockPosts = [
    {
      slug: '10-essential-tools-every-homeowner-should-have',
      title: '10 essentielle værktøj enhver husejer bør have',
      content: `
        <p>At have de rigtige værktøj til rådighed kan gøre hele forskellen, når det kommer til vedligeholdelse og reparationer i hjemmet. Her er vores omfattende guide til de essentielle værktøj, enhver husejer bør have i sin samling.</p>

        <ol style="list-style: none; counter-reset: item; padding-left: 0;">
          <li style="counter-increment: item; margin-bottom: 2rem;">
            <h2 style="font-size: 1.5rem; font-weight: bold; margin-bottom: 0.75rem;"><span style="color: #16a34a; margin-right: 0.5rem;">1.</span>Batteridrevet boremaskine</h2>
            <p style="color: #4b5563; line-height: 1.75;">En god kvalitets batteridrevet boremaskine er nok det mest alsidige el-værktøj, du kan eje. Den er perfekt til alt fra at hænge billeder op til at samle møbler og foretage grundlæggende reparationer i hjemmet.</p>
          </li>
          <li style="counter-increment: item; margin-bottom: 2rem;">
            <h2 style="font-size: 1.5rem; font-weight: bold; margin-bottom: 0.75rem;"><span style="color: #16a34a; margin-right: 0.5rem;">2.</span>Hammer</h2>
            <p style="color: #4b5563; line-height: 1.75;">En klassisk klodhammer er uundværlig både til at hænge billeder op og fjerne søm. Vælg en med et behageligt greb og god balance.</p>
          </li>
          <li style="counter-increment: item; margin-bottom: 2rem;">
            <h2 style="font-size: 1.5rem; font-weight: bold; margin-bottom: 0.75rem;"><span style="color: #16a34a; margin-right: 0.5rem;">3.</span>Skruetrækkersæt</h2>
            <p style="color: #4b5563; line-height: 1.75;">En variation af både fladhovede og krydshovede skruetrækkere vil dække de fleste af dine grundlæggende behov. Overvej at få en multi-bit skruetrækkere for at spare plads.</p>
          </li>
          <li style="counter-increment: item; margin-bottom: 2rem;">
            <h2 style="font-size: 1.5rem; font-weight: bold; margin-bottom: 0.75rem;"><span style="color: #16a34a; margin-right: 0.5rem;">4.</span>Målebånd</h2>
            <p style="color: #4b5563; line-height: 1.75;">Essentiel til alt fra at hænge billeder op til at købe møbler. Få en, der er mindst 7-8 meter lang med en låsemekanisme.</p>
          </li>
          <li style="counter-increment: item; margin-bottom: 2rem;">
            <h2 style="font-size: 1.5rem; font-weight: bold; margin-bottom: 0.75rem;"><span style="color: #16a34a; margin-right: 0.5rem;">5.</span>Vaterpas</h2>
            <p style="color: #4b5563; line-height: 1.75;">Intet er værre end at hænge et billede eller en hylde op, kun for at opdage, at det er skråt. Et godt vaterpas vil forhindre denne almindelige frustration.</p>
          </li>
        </ol>
      `,
      author: {
        name: 'MoreBot',
        avatar: '/images/us/cto.png',
        role: 'Gør-det-selv ekspert',
      },
      date: '2026-02-12',
      readTime: 2,
      category: 'Hjemmeforbedring',
      imageUrl: '/images/blogPosts/blog_post_1.png',
    },
    {
      slug: 'how-to-properly-maintain-your-power-tools',
      title: 'Sådan vedligeholder du dit el-værktøj korrekt',
      content: `
        <p style="color: #4b5563; line-height: 1.75; margin-bottom: 1.5rem;">Korrekt vedligeholdelse af dit el-værktøj kan forlænge dets levetid betydeligt og sikre optimal ydeevne. Her er de bedste fremgangsmåder for at holde dit værktøj i topstand.</p>

        <ol style="list-style: none; counter-reset: item; padding-left: 0;">
          <li style="counter-increment: item; margin-bottom: 2rem;">
            <h2 style="font-size: 1.5rem; font-weight: bold; margin-bottom: 0.75rem;"><span style="color: #16a34a; margin-right: 0.5rem;">1.</span>Rengøring efter brug</h2>
            <p style="color: #4b5563; line-height: 1.75;">Rengør altid dit værktøj efter hver brug. Fjern støv, snavs og rester med en blød børste eller trykluft. Dette forhindrer ophobning af snavs, der kan beskadige motoren.</p>
          </li>
          <li style="counter-increment: item; margin-bottom: 2rem;">
            <h2 style="font-size: 1.5rem; font-weight: bold; margin-bottom: 0.75rem;"><span style="color: #16a34a; margin-right: 0.5rem;">2.</span>Opbevaring</h2>
            <p style="color: #4b5563; line-height: 1.75;">Opbevar dit værktøj på et tørt sted. Fugt kan forårsage rust og korrosion. Brug originale kasser eller værktøjskasser for bedst beskyttelse.</p>
          </li>
          <li style="counter-increment: item; margin-bottom: 2rem;">
            <h2 style="font-size: 1.5rem; font-weight: bold; margin-bottom: 0.75rem;"><span style="color: #16a34a; margin-right: 0.5rem;">3.</span>Batteripleje</h2>
            <p style="color: #4b5563; line-height: 1.75;">For batteridrevet værktøj: opbevar batterier ved stuetemperatur og opladede til 40-50% hvis de ikke bruges i længere tid. Undgå at lade dem helt aflades.</p>
          </li>
          <li style="counter-increment: item; margin-bottom: 2rem;">
            <h2 style="font-size: 1.5rem; font-weight: bold; margin-bottom: 0.75rem;"><span style="color: #16a34a; margin-right: 0.5rem;">4.</span>Smøring</h2>
            <p style="color: #4b5563; line-height: 1.75;">Smør bevægelige dele jævnligt efter producentens anvisninger. Dette reducerer slitage og holder værktøjet kørende problemfrit.</p>
          </li>
          <li style="counter-increment: item; margin-bottom: 2rem;">
            <h2 style="font-size: 1.5rem; font-weight: bold; margin-bottom: 0.75rem;"><span style="color: #16a34a; margin-right: 0.5rem;">5.</span>Regelmæssig inspektion</h2>
            <p style="color: #4b5563; line-height: 1.75;">Tjek regelmæssigt for slidte dele, løse skruer og beskadigede ledninger. Udskift slidte dele med det samme for at undgå ulykker.</p>
          </li>
        </ol>
      `,
      author: {
        name: 'MoreBot',
        avatar: '/images/us/cto.png',
        role: 'Værktøjsekspert',
      },
      date: '2026-02-12',
      readTime: 3,
      category: 'Værktøjsvedligeholdelse',
      imageUrl: '/images/blogPosts/blog_post_3.png',
    },
    {
      slug: 'building-community-through-tool-sharing',
      title: 'Opbygning af fællesskab gennem værktøjsdeling',
      content: `
        <p style="color: #4b5563; line-height: 1.75; margin-bottom: 1.5rem;">Værktøjsdeling er meget mere end bare at spare penge - det handler om at opbygge stærkere fællesskaber og skabe forbindelser mellem naboer. Her er hvordan værktøjsdelingsplatforme transformerer vores nabolag.</p>

        <ol style="list-style: none; counter-reset: item; padding-left: 0;">
          <li style="counter-increment: item; margin-bottom: 2rem;">
            <h2 style="font-size: 1.5rem; font-weight: bold; margin-bottom: 0.75rem;"><span style="color: #16a34a; margin-right: 0.5rem;">1.</span>Reducer miljøpåvirkning</h2>
            <p style="color: #4b5563; line-height: 1.75;">Ved at dele værktøj reducerer vi behovet for at hver husstand ejer de samme værktøj. Dette betyder færre ressourcer brugt på produktion og mindre affald.</p>
          </li>
          <li style="counter-increment: item; margin-bottom: 2rem;">
            <h2 style="font-size: 1.5rem; font-weight: bold; margin-bottom: 0.75rem;"><span style="color: #16a34a; margin-right: 0.5rem;">2.</span>Økonomiske fordele</h2>
            <p style="color: #4b5563; line-height: 1.75;">Hvorfor købe dyrt specialværktøj du kun bruger én gang? Gennem deling får du adgang til et bredt udvalg af værktøj til en brøkdel af omkostningerne.</p>
          </li>
          <li style="counter-increment: item; margin-bottom: 2rem;">
            <h2 style="font-size: 1.5rem; font-weight: bold; margin-bottom: 0.75rem;"><span style="color: #16a34a; margin-right: 0.5rem;">3.</span>Lær af hinanden</h2>
            <p style="color: #4b5563; line-height: 1.75;">Når du låner værktøj, får du ofte også værdifulde tips og tricks fra ejeren. Dette skaber vidensdeling og styrker fællesskabsbånd.</p>
          </li>
          <li style="counter-increment: item; margin-bottom: 2rem;">
            <h2 style="font-size: 1.5rem; font-weight: bold; margin-bottom: 0.75rem;"><span style="color: #16a34a; margin-right: 0.5rem;">4.</span>Byg tillid</h2>
            <p style="color: #4b5563; line-height: 1.75;">Værktøjsdeling kræver tillid mellem mennesker. Hver succesfuld udveksling styrker tilliden og opbygger stærkere naboskaber.</p>
          </li>
          <li style="counter-increment: item; margin-bottom: 2rem;">
            <h2 style="font-size: 1.5rem; font-weight: bold; margin-bottom: 0.75rem;"><span style="color: #16a34a; margin-right: 0.5rem;">5.</span>Skab forbindelser</h2>
            <p style="color: #4b5563; line-height: 1.75;">Gennem værktøjsdeling møder du nye mennesker i dit nabolag. Disse forbindelser kan føre til varige venskaber og et mere sammentømret fællesskab.</p>
          </li>
        </ol>
      `,
      author: {
        name: 'MoreBot',
        avatar: '/images/us/cto.png',
        role: 'Fællesskabsbygger',
      },
      date: '2026-02-12',
      readTime: 4,
      category: 'Fællesskab',
      imageUrl: '/images/blogPosts/blog_post_2.png',
    },
  ];

  const post = mockPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Artikel ikke fundet</h1>
            <p className="text-xl text-gray-600 mb-8">
              Den artikel du leder efter eksisterer ikke.
            </p>
            <Link to="/blog" className="inline-flex items-center text-green-600 hover:text-green-700">
              <ArrowLeft className="h-5 w-5 mr-2" />
              <span>Tilbage til blog</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('da-DK', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Back Button */}
          <Link to="/blog" className="inline-flex items-center text-green-600 hover:text-green-700 mb-8">
            <ArrowLeft className="h-5 w-5 mr-2" />
            <span>Tilbage til blog</span>
          </Link>

          {/* Article Header */}
          <article className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <img
              src={post.imageUrl}
              alt={post.title}
              className="w-full h-64 object-cover"
            />
            
            <div className="p-8">
              {/* Meta Information */}
              <div className="flex flex-wrap items-center text-sm text-gray-500 mb-4">
                <div className="flex items-center mr-4">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span>{formatDate(post.date)}</span>
                </div>
                <div className="flex items-center mr-4">
                  <Clock className="h-4 w-4 mr-1" />
                  <span>{post.readTime} min læsning</span>
                </div>
                <div className="flex items-center">
                  <Tag className="h-4 w-4 mr-1" />
                  <span>{post.category}</span>
                </div>
              </div>

              {/* Title */}
              <h1 className="text-3xl font-bold text-gray-900 mb-6">
                {post.title}
              </h1>

              {/* Author */}
              <div className="flex items-center mb-8 pb-8 border-b border-gray-200">
                <img
                  src={post.author.avatar}
                  alt={post.author.name}
                  className="h-12 w-12 rounded-full mr-4 object-cover"
                />
                <div>
                  <div className="font-medium text-gray-900">{post.author.name}</div>
                  <div className="text-sm text-gray-500">{post.author.role}</div>
                </div>
              </div>

              {/* Content */}
              <div 
                className="prose max-w-none prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h2:text-gray-900 prose-p:text-gray-600 prose-p:leading-relaxed prose-p:mb-4"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {/* Share Buttons */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex items-center">
                  <span className="text-gray-700 font-medium mr-4">Del denne artikel:</span>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm">
                      <Facebook className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm">
                      <Twitter className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm">
                      <Linkedin className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm">
                      <Share2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </article>

          {/* Newsletter Signup */}
          <div className="mt-8 bg-green-600 rounded-lg shadow-sm text-white p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Kunne du lide denne artikel?</h2>
            <p className="text-green-100 mb-6">
              Tilmeld dig vores nyhedsbrev for at få flere nyttige tips og fællesskabshistorier.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto">
              <div className="flex gap-4 mb-4">
                <input
                  type="email"
                  placeholder="Indtast din email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  className="flex-grow px-4 py-2 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-green-600"
                  required
                />
                <button
                  type="submit"
                  disabled={isSubscribing}
                  className="px-4 py-2 bg-white text-green-600 font-medium rounded-md hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-green-600 whitespace-nowrap disabled:opacity-50"
                >
                  {isSubscribing ? 'Tilmelder...' : 'Tilmeld'}
                </button>
              </div>

              {newsletterError && (
                <div className="rounded-md bg-red-50 p-3 text-left">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{newsletterError}</p>
                    </div>
                  </div>
                </div>
              )}

              {newsletterSuccess && (
                <div className="rounded-md bg-white p-3 text-left">
                  <div className="flex">
                    <MessageSquare className="h-5 w-5 text-green-600" />
                    <div className="ml-3">
                      <p className="text-sm text-green-900">
                        Tak for din tilmelding! Du vil snart modtage vores nyhedsbrev.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;